import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html, literal } from 'lit/static-html.js'
import { property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { nothing } from 'lit'

import { AsSubmitter, WithFocus, compose, anchor } from '#utils/mixins'
import { ariaElements, delegateEvents } from '#utils/directives'
import { LfuiElement } from '#utils/element'
import styles from './lfds-button.css.ts'

const LINK = literal`a`
const BUTTON = literal`button`

/**
 * @name LfdsButton
 * @tag lfds-button
 *
 * @status ready
 *
 * @slot default - Button label
 * @slot start - Before the label. Useful for icons.
 * @slot end - After the label. Useful for icons.
 */
export default class LfdsButton extends compose(
	LfuiElement,
	AsSubmitter,
	WithFocus
) {
	static styles = [LfuiElement.styles, styles]

	@property() variant: 'primary' | 'secondary' = 'primary'
	/**
	 * @deprecatedValue small - Use `medium` instead.
	 * @removal 2026-11-30
	 */
	@property() size: 'small' | 'medium' | 'large' = 'medium'
	@property() type: 'button' | 'submit' | 'reset' | 'menu'
	@property() target: '_blank' | '_self'
	@property() download: string | boolean
	@property() href: string
	@property() rel: string
	@property() name: string
	@property() value: string | number
	@property({ type: Boolean }) disabled: boolean = false
	@property({ attribute: 'help-text' }) helpText: string
	@property({ attribute: 'validation-message' }) validationMessage: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby?: string
	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-describedby' }) srDescribedby?: string
	@property({ attribute: 'sr-controls' }) srControls?: string
	@property({ attribute: 'sr-expanded' }) srExpanded: string
	@property({ attribute: 'sr-pressed' }) srPressed: string
	@property({ attribute: 'sr-current' }) srCurrent: string

	connectedCallback() {
		super.connectedCallback()
		// Prevent the host (custom element) from being exposed as a separate node
		// in the accessibility tree. The inner native control carries the name.
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')
	}

	#preventDisabledClick = (event: Event) => {
		if (this.disabled && this.href != null) {
			event.preventDefault()
		}
	}

	willUpdate() {
		if (this.size === 'small') {
			console.warn(
				'[lfds-button] size="small" is deprecated and will be removed in a future version. Use "medium" instead.'
			)
			this.size = 'medium'
		}
	}

	render() {
		const tag = this.href != null ? LINK : BUTTON
		return html`
      <${tag}
        class=${classMap({
					button: true,
					[this.size]: this.size,
					[this.variant]: this.variant,
					['focus-visible']: this.focusVisible
				})}
        aria-expanded=${
					this.srExpanded != null ? this.srExpanded : (nothing as any)
				}
        aria-pressed=${
					this.srPressed != null ? this.srPressed : (nothing as any)
				}
        aria-current=${
					this.srCurrent != null ? this.srCurrent : (nothing as any)
				}
        rel=${ifDefined(this.rel)}
        href=${ifDefined(this.href)}
        name=${ifDefined(this.name)}
        type=${ifDefined(this.type)}
        value=${ifDefined(this.value)}
        target=${ifDefined(this.target)}
        aria-label=${ifDefined(this.srLabel)}
        download=${this.download ? this.download || '' : nothing}
        ?disabled=${tag === BUTTON && this.disabled}
        aria-disabled=${tag === LINK && this.disabled ? 'true' : nothing}
        tabindex=${tag === LINK && this.disabled ? -1 : nothing}
        ${ref(this[anchor])}
        ${delegateEvents(this.delegatedEvents, {
					before: { click: this.#preventDisabledClick },
					after: { click: this.handleClick }
				})}
        ${ariaElements({
					labelledBy: this.srLabelledby,
					describedBy: this.srDescribedby,
					controls: this.srControls
				})}>
        <slot name="start"></slot>
        <span class="main">
          <slot>
            ${
							this.helpText
								? html`<span class="help">${this.helpText}</span>`
								: nothing
						}
          </slot>
        </span>
        <slot name="end"></slot>
      </${tag}>
    `
	}
}
